import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import {
  ApiError, addOrgSite, createArea, createOrg, deleteArea, fmtTime, grantEntitlement, invitePerson,
  resendInvite, revokeEntitlement, revokeMembership, searchRegister,
  setSiteOwner, startViewAs,
  type Area, type Entitlement, type Me, type MemberRole, type OrgDetail,
  type OrgList, type OrgSite, type Person, type Product,
  type RegisterCompany,
} from '../api'
import { Codes, Devices, EnrollWindow, Networks } from './Enheter'
import { invalidate, useApi } from '../useApi'
import { Empty, ErrorBox, Field, Rows } from '../components/bits'
import { PageHead } from '../components/Shell'
import { Link } from '../router'
import { LocalityPicker, type Place } from '../components/LocalityPicker'
import { useT } from '../lang'
import { LiceBookOrders } from '../components/LiceBookOrders'

/* Setting up a customer: the company, its farms, its people, and what it has
 * bought.
 *
 * Staff only, and reached from the rail foot rather than from NAV. NAV drives
 * the mobile tab bar, whose five slots are the five things people came here to
 * do; onboarding a company is a thing done once per customer at a desk.
 *
 * The order of the sections on the detail page is the order the work happens
 * in, and it is not arbitrary. A farm with no owner is invisible to the
 * customer paying for it, and a person invited before their company owns a
 * farm signs in to an empty screen -- so Sites comes before People, and the
 * page says so when the earlier step has not been done.
 */

const ROLES: { value: MemberRole; label: string; hint: string }[] = [
  { value: 'company_manager', label: 'Company manager',
    hint: 'Every farm the company owns, now and in future. May invite colleagues.' },
  { value: 'area_manager', label: 'Area manager',
    hint: 'The areas you name, and everything nested inside them.' },
  { value: 'site_manager', label: 'Site manager', hint: 'The farms you name.' },
  { value: 'ship', label: 'Ship',
    hint: 'Named farms, listening only, and the access expires.' },
  { value: 'subsupplier', label: 'Subsupplier',
    hint: 'Named farms, listening only, and the access expires.' },
]

function why(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not reach the receiver.'
  return err.detail ?? err.code
}

/* ------------------------------------------------------------ the list */

export function Operators({ me }: { me: Me }) {
  const t = useT()
  const q = useApi<OrgList>('/v1/orgs')
  const [adding, setAdding] = useState(false)

  if (q.cold) return <Rows n={5} />
  if (q.error) return <ErrorBox error={q.error} />
  const d = q.data
  if (!d) return null

  return (
    <>
      <PageHead
        title={t('Operators')}
        lead={t('The companies that own the farms, and the people who work there.')}
        cta={me.can_write && (
          <button className="btn primary" onClick={() => setAdding(!adding)}>
            {adding ? t('Cancel') : t('Add a company')}
          </button>
        )}
      />

      {adding && <NewOrg onDone={() => { setAdding(false); q.reload() }} />}

      <LiceBookOrders canWrite={!!me.can_write} />

      {/* Shown before the list, because it is the thing blocking every
          customer login: a farm with no owner cannot be seen by anybody
          outside Phoniq, however many people are invited. */}
      {/* Counts only farms where a real deployment has happened. Trial rigs,
          benches and desks never acquire one, so they never appear here --
          which is what keeps this a number worth reading rather than a
          permanent warning everybody learns to scroll past. */}
      {d.sites_without_owner > 0 && (
        <div className="notice">
          <strong>{d.sites_without_owner}</strong>{' '}
          {d.sites_without_owner === 1
            ? t('deployed farm has no owner, so no customer can see it.')
            : t('deployed farms have no owner, so no customer can see them.')}
          {d.unassigned_operators.length > 0 && (
            <> {t('Typed at commissioning as:')}{' '}
              {d.unassigned_operators.join(', ')}.</>
          )}
        </div>
      )}

      {d.items.length === 0 ? (
        <Empty title={t('No companies yet')}>
          {t('Add one, give it a farm, then invite somebody who works there.')}
        </Empty>
      ) : (
        <div className="card">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('Company')}</th><th>{t('Farms')}</th>
                <th>{t('People')}</th><th>{t('Areas')}</th>
              </tr>
            </thead>
            <tbody>
              {d.items.map((o) => (
                <tr key={o.org_id}>
                  <td>
                    <Link to={`/operators/${encodeURIComponent(o.org_id)}`}>
                      {o.name}
                    </Link>
                    {o.org_no && <div className="faint" style={{ fontSize: 12 }}>
                      {t('Org. no.')} {o.org_no}</div>}
                  </td>
                  <td>{o.site_count}</td>
                  <td>{o.member_count}</td>
                  <td>{o.area_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

function NewOrg({ onDone }: { onDone: () => void }) {
  const t = useT()
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<RegisterCompany[]>([])
  const [picked, setPicked] = useState<RegisterCompany | null>(null)
  const [manual, setManual] = useState(false)
  const [name, setName] = useState('')
  const [orgNo, setOrgNo] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* Debounced, and the debounce is the whole reason this feels like a picker
     rather than a form. The index is cached on the receiver, so a keystroke
     costs one same-origin request against 532 rows in memory -- but firing on
     every character still means eight requests to type "nordlaks", and the
     answers can arrive out of order. `live` discards the stale one. */
  useEffect(() => {
    if (picked || manual || q.trim().length < 2) { setHits([]); return }
    let live = true
    const id = setTimeout(() => {
      searchRegister(q.trim())
        .then((r) => { if (live) setHits(r.items) })
        .catch(() => { if (live) setHits([]) })
    }, 180)
    return () => { live = false; clearTimeout(id) }
  }, [q, picked, manual])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      await createOrg(picked
        ? { name: picked.name,
            org_no: picked.org_nr ?? undefined,
            register_slug: picked.slug }
        // No organisasjonsnummer required. A foreign subsupplier has none, a
        // research partner has one nobody needs, and demanding it would make
        // a Norwegian register a precondition for having a customer.
        : { name, org_no: orgNo || undefined })
      invalidate('/v1/orgs')
      onDone()
    } catch (err) {
      setError(why(err)); setBusy(false)
    }
  }

  return (
    <form className="card stack" onSubmit={submit} style={{ gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18 }}>{t('New company')}</h2>

      {!manual && !picked && (
        <>
          <Field label="Search the aquaculture register"
            hint={t('Type a company name or organisasjonsnummer.')}>
            <input value={q} autoFocus autoComplete="off"
              onChange={(e) => setQ(e.target.value)} />
          </Field>

          {hits.length > 0 && (
            <div className="stack" style={{ gap: 2 }}>
              {hits.map((h) => (
                <button key={h.slug} type="button" className="btn ghost"
                  style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  onClick={() => setPicked(h)}>
                  <span>
                    {h.name}
                    <span className="faint" style={{ fontSize: 12 }}>
                      {'  '}{h.org_nr}
                      {h.municipality ? ` · ${h.municipality}` : ''}
                      {h.n_sites ? ` · ${h.n_sites} ${t('farms')}` : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* The register holds 532 companies with farms. A research partner,
              a test customer or a foreign subsupplier is in none of them, and
              refusing to create one would make an external register a
              precondition for using this product. */}
          <button type="button" className="btn ghost sm"
            onClick={() => setManual(true)}>
            {t('Not in the register — enter a name instead')}
          </button>
        </>
      )}

      {picked && (
        <div className="notice">
          <strong>{picked.name}</strong>
          <div className="muted" style={{ fontSize: 14 }}>
            {t('Org. no.')} {picked.org_nr}
            {picked.municipality ? ` · ${picked.municipality}` : ''}
            {picked.county ? `, ${picked.county}` : ''}
          </div>
          {/* Said out loud, because it is the reason to pick rather than type:
              the slug is what joins this customer to the register's cohort,
              vessel and lice data, and a name typed by hand joins to nothing. */}
          <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
            {t('Linked to the register, so its farms, vessels and lice data can be matched.')}
          </div>
          <button type="button" className="btn ghost sm" style={{ marginTop: 8 }}
            onClick={() => { setPicked(null); setQ('') }}>
            {t('Choose a different company')}
          </button>
        </div>
      )}

      {manual && (
        <>
          <Field label="Name">
            <input value={name} required autoFocus
              onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Organisasjonsnummer"
            hint={t('Optional. Leave it blank if the company has none.')}>
            <input value={orgNo} inputMode="numeric"
              onChange={(e) => setOrgNo(e.target.value)} />
          </Field>
          <button type="button" className="btn ghost sm"
            onClick={() => { setManual(false); setName(''); setOrgNo('') }}>
            {t('Search the register instead')}
          </button>
        </>
      )}

      {error && <div className="notice bad">{error}</div>}
      <div>
        <button className="btn primary" type="submit"
          disabled={busy || (!picked && !name)}>
          {busy ? t('Saving…') : t('Create')}
        </button>
      </div>
    </form>
  )
}
/* ------------------------------------------------------- the detail page */

export function OperatorPage({ id, me }: { id: string; me: Me }) {
  const t = useT()
  const path = `/v1/orgs/${encodeURIComponent(id)}`
  const q = useApi<OrgDetail>(path)

  if (q.cold) return <Rows n={6} />
  if (q.error) return <ErrorBox error={q.error} />
  const org = q.data
  if (!org) return null

  const reload = () => { invalidate(path); q.reload() }

  return (
    <>
      <PageHead
        title={org.name}
        lead={[org.org_no && `${t('Org. no.')} ${org.org_no}`,
               `${org.sites.length} ${t('farms')}`].filter(Boolean).join(' · ')}
        cta={me.can_write && (
          /* Instead of inventing a fake account with a fake address to sign in
             as -- which leaves a real credential belonging to nobody inside a
             customer's company, and which somebody always forgets to delete. */
          <button className="btn" onClick={() => void startViewAs(org.org_id)
            .then(() => window.location.assign('/'))}>
            {t('View as this customer')}
          </button>
        )}
      />
      <SitesCard org={org} me={me} onChanged={reload} />
      <AreasCard org={org} me={me} onChanged={reload} />
      <PeopleCard org={org} me={me} />
      <ProductsCard org={org} me={me} />
      {/* The same three cards the customer gets at /enheter, rendered here
          against the org in the path rather than against the signed-in
          person's memberships.
          Reused rather than reimplemented, and that is the whole point: a
          second enrollment screen for staff would be a second place for
          "what does this code grant" to be answered, and the two would
          disagree the first time either changed. Enheter owns them; this page
          borrows them.
          Last, and after People deliberately. _mint refuses with 409
          no_manager when a company has nobody to notify, and a code scoped to
          a farm needs that farm owned first -- so both of the steps these
          cards depend on are above them on the page, in the order the work
          actually happens. */}
      {/* Only for someone the receiver lets manage people, and not on a
          disabled company. Otherwise every card is a 404 box, and the window's
          hour buttons look live and answer 404 when pressed. */}
      {me.can?.manage_people && !org.disabled_at && (
        <>
          <Devices orgId={org.org_id} />
          <EnrollWindow orgId={org.org_id} />
          <Codes orgId={org.org_id} />
          <Networks orgId={org.org_id} />
        </>
      )}
    </>
  )
}

/* ----------------------------------------------------------------- sites */

interface SiteRow {
  site_id: string
  name: string
  operator: string | null
  org_id: string | null
  org_name: string | null
  area_id: string | null
}

function SitesCard({ org, me, onChanged }: {
  org: OrgDetail; me: Me; onChanged: () => void
}) {
  const t = useT()
  /* `unowned=1` and not every site filtered in the browser.
   *
   * This used to fetch the whole fleet and keep the ownerless rows, which on
   * this system is fifteen rows of test residue -- four "Bergen Nord
   * 178525xxxx", five "Verification site", an "Etag Test" -- above one real
   * farm. Every one of them carried a "Give to <company>" button, so the
   * screen offered a destructive one-click action fifteen times where fourteen
   * of the offers were wrong, and the real one was buried in them.
   *
   * The receiver decides what a farm is: a hydrophone was actually deployed
   * here, or the aquaculture register issued this place a locality number.
   * Same predicate that produces the count on the company list, so the screen
   * saying "1 farm has no owner" and the screen listing them cannot disagree.
   */
  const all = useApi<{ items: SiteRow[] }>('/v1/sites?unowned=1&limit=200')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const unowned = all.data?.items ?? []

  const assign = async (siteId: string, areaId: string | null) => {
    setBusy(siteId); setError(null)
    try {
      await setSiteOwner(siteId, { org_id: org.org_id, area_id: areaId })
      invalidate('/v1/sites'); invalidate('/v1/orgs')
      all.reload(); onChanged()
    } catch (err) { setError(why(err)) } finally { setBusy(null) }
  }

  const release = async (siteId: string) => {
    setBusy(siteId); setError(null)
    try {
      await setSiteOwner(siteId, { org_id: null, area_id: null })
      invalidate('/v1/sites'); invalidate('/v1/orgs')
      all.reload(); onChanged()
    } catch (err) { setError(why(err)) } finally { setBusy(null) }
  }

  return (
    <div className="card stack" style={{ gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18 }}>{t('Farms')}</h2>
      {error && <div className="notice bad">{error}</div>}

      {org.sites.length === 0 ? (
        <Empty title={t('No farms yet')}>
          {t('Until this company owns a farm, nobody here can see anything.')}
        </Empty>
      ) : (
        <table className="tbl">
          <thead>
            <tr><th>{t('Farm')}</th><th>{t('Area')}</th><th></th></tr>
          </thead>
          <tbody>
            {org.sites.map((s) => (
              <tr key={s.site_id}>
                <td>
                  <Link to={`/sites/${encodeURIComponent(s.site_id)}`}>{s.name}</Link>
                  {s.locality_no && <div className="faint" style={{ fontSize: 12 }}>
                    {t('Locality')} {s.locality_no}
                    {s.municipality ? ` · ${s.municipality}` : ''}</div>}
                </td>
                <td>
                  {me.can_write ? (
                    <select value={s.area_id ?? ''} disabled={busy === s.site_id}
                      onChange={(e) => void assign(s.site_id, e.target.value || null)}>
                      <option value="">{t('No area')}</option>
                      {org.areas.map((a) => (
                        <option key={a.area_id} value={a.area_id}>{a.name}</option>
                      ))}
                    </select>
                  ) : (org.areas.find((a) => a.area_id === s.area_id)?.name ?? '—')}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {/* Onboarding's second half, next to the farm it belongs to.
                      Giving a company a farm makes it visible; drawing its
                      rings is what makes it a lice product, and the two acts
                      happen in one sitting with the customer on the phone. */}
                  <Link className="btn sm"
                    to={`/sites/${encodeURIComponent(s.site_id)}/oppsett`}>
                    {t('Set up')}
                  </Link>
                  {me.can_write && (
                    <button className="btn ghost sm" disabled={busy === s.site_id}
                      style={{ marginLeft: 6 }}
                      onClick={() => void release(s.site_id)}>
                      {t('Remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {me.can_write && <AddFarm org={org} onAdded={() => { all.reload(); onChanged() }} />}

      {me.can_write && unowned.length > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="muted" style={{ fontSize: 14 }}>
            {t('Farms with no owner')}
          </div>
          {unowned.map((s) => (
            <div key={s.site_id} style={{ display: 'flex', gap: 10,
              alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                {s.name}
                {/* What somebody typed at commissioning. It is the only clue
                    to who this farm belongs to, so it is shown next to the
                    button that decides. */}
                {s.operator && <span className="faint" style={{ fontSize: 12 }}>
                  {' · '}{t('typed as')} {s.operator}</span>}
              </div>
              <button className="btn sm" disabled={busy === s.site_id}
                onClick={() => void assign(s.site_id, null)}>
                {t('Give to')} {org.name}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** A farm is picked from the locality register and lands owned, in its area,
 *  in one step. Without this the only form on the page made areas, and a farm
 *  typed into it became an area that then held some other farm's map. */
function AddFarm({ org, onAdded }: { org: OrgDetail; onAdded: () => void }) {
  const t = useT()
  const [place, setPlace] = useState<Place | null>(null)
  const [area, setArea] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [added, setAdded] = useState<{ site_id: string; name: string } | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (place?.locality_no == null) return
    setBusy(true); setError(null); setAdded(null)
    try {
      const r = await addOrgSite(org.org_id,
        { locality_no: place.locality_no, area_id: area || null })
      invalidate('/v1/sites'); invalidate('/v1/orgs')
      setAdded(r); setPlace(null); setArea(''); onAdded()
    } catch (err) { setError(why(err)) } finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit} className="stack" style={{ gap: 12 }}>
      <div className="muted" style={{ fontSize: 14 }}>{t('Add farm')}</div>
      <LocalityPicker value={place} onChange={setPlace} />
      {place && place.locality_no == null && (
        <div className="notice">
          {t('A farm is added from the locality register. Search for it by name or number.')}
        </div>
      )}
      {org.areas.length > 0 && (
        <Field label="Area">
          <select value={area} onChange={(e) => setArea(e.target.value)}>
            <option value="">{t('No area')}</option>
            {org.areas.map((a) => (
              <option key={a.area_id} value={a.area_id}>{a.name}</option>
            ))}
          </select>
        </Field>
      )}
      {error && <div className="notice bad">{error}</div>}
      {added && (
        <div className="notice">
          {added.name} {t('added.')}{' '}
          <Link to={`/sites/${encodeURIComponent(added.site_id)}/oppsett`}>
            {t('Set up')}
          </Link>
        </div>
      )}
      <div>
        <button className="btn" type="submit"
          disabled={busy || place?.locality_no == null}>
          {busy ? t('Saving…') : t('Add farm')}
        </button>
      </div>
    </form>
  )
}

/* ----------------------------------------------------------------- areas */

function AreasCard({ org, me, onChanged }: {
  org: OrgDetail; me: Me; onChanged: () => void
}) {
  const t = useT()
  const [name, setName] = useState('')
  const [parent, setParent] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const remove = async (areaId: string) => {
    setBusy(true); setError(null)
    try {
      await deleteArea(areaId)
      onChanged()
    } catch (err) { setError(why(err)) } finally { setBusy(false) }
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      await createArea({ org_id: org.org_id, name,
                         parent_area_id: parent || null })
      setName(''); setParent(''); onChanged()
    } catch (err) { setError(why(err)) } finally { setBusy(false) }
  }

  // Depth for indentation, so a nested area reads as nested.
  const depth = (a: Area): number => {
    let n = 0, cur = a.parent_area_id
    while (cur) {
      n++
      cur = org.areas.find((x) => x.area_id === cur)?.parent_area_id ?? null
      if (n > 8) break
    }
    return n
  }

  return (
    <div className="card stack" style={{ gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18 }}>{t('Areas')}</h2>
      <p className="muted" style={{ margin: 0, fontSize: 14 }}>
        {t('How this company groups its farms. An area manager sees their areas and everything nested inside them.')}
        {' '}{t('An area is not a farm: add farms in the Farms card above.')}
      </p>

      {org.areas.length > 0 && (
        <div className="stack" style={{ gap: 4 }}>
          {org.areas.map((a) => (
            <div key={a.area_id} style={{ paddingLeft: depth(a) * 18 }}>
              {depth(a) > 0 && <span className="faint">↳ </span>}
              {a.name}{' '}
              <span className="faint" style={{ fontSize: 12 }}>
                {a.site_count} {t('farms')}
              </span>
              {me.can_write && a.site_count === 0
                && !org.areas.some((x) => x.parent_area_id === a.area_id) && (
                <button className="btn ghost sm" disabled={busy}
                  style={{ marginLeft: 6 }}
                  onClick={() => void remove(a.area_id)}>
                  {t('Remove')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {me.can_write && (
        <form onSubmit={submit} className="stack" style={{ gap: 12 }}>
          <Field label="New area">
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          {org.areas.length > 0 && (
            <Field label="Inside">
              <select value={parent} onChange={(e) => setParent(e.target.value)}>
                <option value="">{t('Top level')}</option>
                {org.areas.map((a) => (
                  <option key={a.area_id} value={a.area_id}>{a.name}</option>
                ))}
              </select>
            </Field>
          )}
          {error && <div className="notice bad">{error}</div>}
          <div>
            <button className="btn" type="submit" disabled={busy || !name}>
              {busy ? t('Saving…') : t('Add area')}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- people */

function PeopleCard({ org, me }: { org: OrgDetail; me: Me }) {
  const t = useT()
  const path = `/v1/people?org_id=${encodeURIComponent(org.org_id)}`
  const q = useApi<{ items: Person[] }>(path)
  const [inviting, setInviting] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reload = () => { invalidate(path); q.reload() }
  const may = me.can_write || me.can?.manage_people

  const resend = async (userId: string) => {
    setBusy(userId); setError(null); setNote(null)
    try {
      await resendInvite(org.org_id, userId)
      setNote(t('Invitation sent again.'))
      reload()
    } catch (err) { setError(why(err)) } finally { setBusy(null) }
  }

  const revoke = async (userId: string, email: string) => {
    if (!confirm(t('Remove access for') + ` ${email}?`)) return
    setBusy(userId); setError(null); setNote(null)
    try {
      await revokeMembership(org.org_id, userId)
      reload()
    } catch (err) { setError(why(err)) } finally { setBusy(null) }
  }

  const people = q.data?.items ?? []

  return (
    <div className="card stack" style={{ gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{t('People')}</h2>
        {may && (
          <button className="btn sm" onClick={() => setInviting(!inviting)}>
            {inviting ? t('Cancel') : t('Invite somebody')}
          </button>
        )}
      </div>

      {org.sites.length === 0 && (
        <div className="notice">
          {t('This company owns no farms yet, so anybody invited now signs in to an empty screen.')}
        </div>
      )}

      {inviting && (
        <InviteForm org={org}
          onDone={() => { setInviting(false); reload() }} />
      )}

      {note && <div className="notice">{note}</div>}
      {error && <div className="notice bad">{error}</div>}
      {q.cold && <Rows n={2} />}

      {!q.cold && people.length === 0 ? (
        <Empty title={t('Nobody yet')}>
          {t('An invitation creates an account with no password and mails a single-use link.')}
        </Empty>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('Person')}</th><th>{t('Role')}</th>
              <th>{t('Status')}</th><th></th>
            </tr>
          </thead>
          <tbody>
            {people.map((p) => (
              <tr key={p.user_id}>
                <td>
                  {p.display_name || p.email}
                  {p.display_name && <div className="faint" style={{ fontSize: 12 }}>
                    {p.email}</div>}
                </td>
                <td>
                  {ROLES.find((r) => r.value === p.member_role)?.label
                   ?? p.member_role}
                  {p.expires_at && (
                    <div className="faint" style={{ fontSize: 12 }}>
                      {t('until')} {fmtTime(p.expires_at)}
                    </div>
                  )}
                  {(p.area_ids.length > 0 || p.site_ids.length > 0) && (
                    <div className="faint" style={{ fontSize: 12 }}>
                      {p.area_ids.length + p.site_ids.length} {t('scoped')}
                    </div>
                  )}
                </td>
                <td>
                  {/* The difference between "we asked them" and "they are
                      using it" -- the first thing anyone wants to know when a
                      customer says nobody can get in. */}
                  {p.awaiting_setup
                    ? <span className="muted">{t('Not set up yet')}</span>
                    : p.last_login_at
                      ? <span className="faint">{fmtTime(p.last_login_at)}</span>
                      : <span className="faint">{t('Never signed in')}</span>}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {may && p.awaiting_setup && (
                    <button className="btn ghost sm" disabled={busy === p.user_id}
                      onClick={() => void resend(p.user_id)}>
                      {t('Resend')}
                    </button>
                  )}
                  {may && (
                    <button className="btn ghost sm" disabled={busy === p.user_id}
                      onClick={() => void revoke(p.user_id, p.email)}>
                      {t('Remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function InviteForm({ org, onDone }: { org: OrgDetail; onDone: () => void }) {
  const t = useT()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<MemberRole>('site_manager')
  const [siteIds, setSiteIds] = useState<string[]>([])
  const [areaIds, setAreaIds] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const scoped = role !== 'company_manager'
  const usesAreas = role === 'area_manager'

  // Changing the role changes what a scope even means, so the old selection is
  // dropped rather than silently carried into a question it does not answer.
  useEffect(() => { setSiteIds([]); setAreaIds([]) }, [role])

  const toggle = (list: string[], set: (v: string[]) => void, id: string) =>
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      await invitePerson({
        email, display_name: name || undefined, org_id: org.org_id,
        member_role: role, area_ids: areaIds, site_ids: siteIds,
      })
      onDone()
    } catch (err) { setError(why(err)); setBusy(false) }
  }

  // Flattened to {id, name} before rendering: a union of Area[] and OrgSite[]
  // cannot be indexed generically without narrowing at every use.
  const choices: { id: string; name: string }[] = usesAreas
    ? org.areas.map((a: Area) => ({ id: a.area_id, name: a.name }))
    : org.sites.map((s: OrgSite) => ({ id: s.site_id, name: s.name }))
  const list = usesAreas ? areaIds : siteIds
  const set = usesAreas ? setAreaIds : setSiteIds

  const chosen = ROLES.find((r) => r.value === role)!
  const ready = email && (!scoped || areaIds.length > 0 || siteIds.length > 0)

  return (
    <form onSubmit={submit} className="stack"
      style={{ gap: 14, borderLeft: '3px solid var(--signal, #B4D9C7)',
               paddingLeft: 14 }}>
      <Field label="Email">
        <input type="email" value={email} required autoFocus
          onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field label="Name" hint={t('Optional, and only used to address the invitation.')}>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Role" hint={chosen.hint}>
        <select value={role}
          onChange={(e) => setRole(e.target.value as MemberRole)}>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </Field>

      {scoped && (
        <Field label={usesAreas ? 'Areas' : 'Farms'}>
          <div className="stack" style={{ gap: 4 }}>
            {choices.map((x) => (
              <label key={x.id} style={{ display: 'flex', gap: 8,
                                         alignItems: 'center' }}>
                <input type="checkbox" checked={list.includes(x.id)}
                  onChange={() => toggle(list, set, x.id)} />
                {x.name}
              </label>
            ))}
            {choices.length === 0 && (
              <span className="faint">
                {usesAreas ? t('No areas yet.') : t('No farms yet.')}
              </span>
            )}
          </div>
        </Field>
      )}

      {(role === 'ship' || role === 'subsupplier') && (
        <div className="notice">
          {t('Access for a vessel or a contractor expires after 90 days. A shared account in a wheelhouse should not outlive the job.')}
        </div>
      )}

      {error && <div className="notice bad">{error}</div>}
      <div>
        <button className="btn primary" type="submit" disabled={busy || !ready}>
          {busy ? t('Sending…') : t('Send invitation')}
        </button>
      </div>
    </form>
  )
}

/* -------------------------------------------------------------- products */

function ProductsCard({ org, me }: { org: OrgDetail; me: Me }) {
  const t = useT()
  const path = `/v1/entitlements?org_id=${encodeURIComponent(org.org_id)}`
  const grants = useApi<{ items: Entitlement[] }>(path)
  const catalogue = useApi<{ items: Product[] }>('/v1/products')
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reload = () => { invalidate(path); grants.reload() }

  const drop = async (id: string) => {
    setBusy(id); setError(null)
    try { await revokeEntitlement(id); reload() }
    catch (err) { setError(why(err)) } finally { setBusy(null) }
  }

  const live = (grants.data?.items ?? []).filter((g) => !g.revoked_at)

  return (
    <div className="card stack" style={{ gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{t('Products')}</h2>
        {me.can_write && (
          <button className="btn sm" onClick={() => setAdding(!adding)}>
            {adding ? t('Cancel') : t('Grant a product')}
          </button>
        )}
      </div>

      {adding && catalogue.data && (
        <GrantForm org={org} products={catalogue.data.items}
          onDone={() => { setAdding(false); reload() }} />
      )}

      {error && <div className="notice bad">{error}</div>}
      {grants.cold && <Rows n={2} />}

      {!grants.cold && live.length === 0 ? (
        <Empty title={t('Nothing granted')}>
          {t('A farm with no products shows its people nothing but the deployment itself.')}
        </Empty>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('Product')}</th><th>{t('Applies to')}</th>
              <th>{t('Included')}</th><th></th>
            </tr>
          </thead>
          <tbody>
            {live.map((g) => (
              <tr key={g.entitlement_id}>
                <td>{g.product_name}</td>
                <td>
                  {/* Company-wide covers farms bought next year too, which is
                      usually what somebody means. Said plainly so the two are
                      not confused at a glance. */}
                  {g.site_id
                    ? g.site_name
                    : <span className="muted">{t('Every farm')}</span>}
                  {g.valid_to && <div className="faint" style={{ fontSize: 12 }}>
                    {t('until')} {fmtTime(g.valid_to)}</div>}
                </td>
                <td className="faint" style={{ fontSize: 12 }}>
                  {g.features.length ? g.features.join(', ') : t('base only')}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {me.can_write && (
                    <button className="btn ghost sm"
                      disabled={busy === g.entitlement_id}
                      onClick={() => void drop(g.entitlement_id)}>
                      {t('Revoke')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function GrantForm({ org, products, onDone }: {
  org: OrgDetail; products: Product[]; onDone: () => void
}) {
  const t = useT()
  const [productId, setProductId] = useState(products[0]?.product_id ?? '')
  const [siteId, setSiteId] = useState('')
  const [features, setFeatures] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const product = products.find((p) => p.product_id === productId)

  // A feature key belongs to one product; carrying a selection across would
  // send keys the server correctly refuses.
  useEffect(() => {
    setFeatures(product ? product.features.map((f) => f.feature_key) : [])
  }, [productId])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      await grantEntitlement({
        product_id: productId,
        org_id: siteId ? null : org.org_id,
        site_id: siteId || null,
        features,
      })
      onDone()
    } catch (err) { setError(why(err)); setBusy(false) }
  }

  return (
    <form onSubmit={submit} className="stack"
      style={{ gap: 14, borderLeft: '3px solid var(--signal, #B4D9C7)',
               paddingLeft: 14 }}>
      <Field label="Product">
        <select value={productId} onChange={(e) => setProductId(e.target.value)}>
          {products.map((p) => (
            <option key={p.product_id} value={p.product_id}>{p.name}</option>
          ))}
        </select>
      </Field>
      {product?.summary && (
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>{product.summary}</p>
      )}

      <Field label="Applies to"
        hint={t('A company-wide grant covers farms bought later too.')}>
        <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="">{t('Every farm this company owns')}</option>
          {org.sites.map((s) => (
            <option key={s.site_id} value={s.site_id}>{s.name}</option>
          ))}
        </select>
      </Field>

      {product && product.features.length > 0 && (
        <Field label="Included">
          <div className="stack" style={{ gap: 4 }}>
            {product.features.map((f) => (
              <label key={f.feature_key}
                style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={features.includes(f.feature_key)}
                  onChange={() => setFeatures(
                    features.includes(f.feature_key)
                      ? features.filter((x) => x !== f.feature_key)
                      : [...features, f.feature_key])} />
                {f.name}
              </label>
            ))}
          </div>
        </Field>
      )}

      {error && <div className="notice bad">{error}</div>}
      <div>
        <button className="btn primary" type="submit" disabled={busy || !productId}>
          {busy ? t('Saving…') : t('Grant')}
        </button>
      </div>
    </form>
  )
}
